import type { MemberDto } from "@ied/shared-types";
import {
	createColumnHelper,
	getCoreRowModel,
	type TableOptions,
} from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { TanstackTable } from "../../ui/table";
import { useMemberTableContext } from "./members-table-provider";
import { TableOptions as TableOptionsBar } from "./table-options";

export function MembersTable() {
	const {
		members,
		nameToAbbreviationMap,
		pageIndex,
		setPageIndex,
		pageSize,
		setPageSize,
	} = useMemberTableContext();

	const handlePaginationChange = useCallback(
		(state: { pageIndex: number; pageSize: number }) => {
			setPageIndex(state.pageIndex);
			setPageSize(state.pageSize);
		},
		[setPageIndex, setPageSize],
	);

	const hasNoMembers = !members || members.items.length === 0;

	const cHelper = createColumnHelper<MemberDto>();
	const columns = useMemo(
		() => [
			cHelper.accessor("id", {
				header: () => "Id",
			}),
			cHelper.accessor("first_name", {
				header: () => "First Name",
			}),
			cHelper.accessor("last_name", {
				header: () => "Last Name",
			}),
			cHelper.accessor("email", {
				header: () => "Email",
				cell: (row) => (
					<a
						className="underline text-blue-500"
						href={`mailto:${row.getValue()}`}
					>
						{row.getValue()}
					</a>
				),
			}),
			cHelper.accessor("phone", {
				header: () => "Phone",
			}),
			cHelper.accessor("groups", {
				header: () => "Groups",
				cell: (props) => {
					const groups = props.getValue();
					if (!groups || groups.length === 0) {
						return "-";
					}
					return groups.map((g) => nameToAbbreviationMap?.[g]).join(", ");
				},
			}),
			cHelper.accessor("is_active", {
				header: () => "Active",
				cell: (props) => {
					return props.getValue() ? "Yes" : "No";
				},
			}),
			cHelper.accessor("created", {
				header: () => "Created",
				cell: (row) => (
					<div>
						<p>{new Date(row.getValue()).toLocaleDateString()}</p>
						<p>{new Date(row.getValue()).toLocaleTimeString()}</p>
					</div>
				),
			}),
			cHelper.accessor("updated", {
				header: () => "Updated",
				cell: (row) => (
					<div>
						<p>{new Date(row.getValue()).toLocaleDateString()}</p>
						<p>{new Date(row.getValue()).toLocaleTimeString()}</p>
					</div>
				),
			}),
		],
		[cHelper, nameToAbbreviationMap],
	);

	const tableOptions: TableOptions<MemberDto> = {
		columns,
		data: members?.items ?? [],
		getCoreRowModel: getCoreRowModel(),
	};

	return (
		<div className="space-y-2">
			<TableOptionsBar />
			{hasNoMembers ? (
				<p>No members</p>
			) : (
				<TanstackTable
					options={tableOptions}
					paginationOptions={{
						isPaginated: true,
						pageIndex,
						pageSize,
						totalCount: members?.totalItems ?? 0,
						onPaginationChange: handlePaginationChange,
					}}
				/>
			)}
		</div>
	);
}
